import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePurchaseBills } from '@hooks/useProcurement'

export function PurchaseBillListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = usePurchaseBills(search, status)

  const bills = data?.data?.items || []
  const total = data?.data?.total || 0
  const pageSize = data?.data?.pageSize || 20
  const totalPages = data?.data?.totalPages || 0

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Bills</h1>
          <p className="text-gray-600 text-sm mt-1">
            Record supplier bills and create stock batches
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/procurement/purchase-bills/create')}
          className="btn-primary"
        >
          + Create Bill
        </button>
      </div>

      <div className="card">
        <div className="space-y-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              type="text"
              placeholder="Search by bill number or supplier..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-full"
            />

            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value)
                setPage(1)
              }}
              className="w-full"
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            Failed to load purchase bills. Please try again.
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : bills.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No purchase bills found.' : 'No purchase bills yet.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-2 font-semibold">Bill No.</th>
                    <th className="text-left px-4 py-2 font-semibold">Supplier</th>
                    <th className="text-left px-4 py-2 font-semibold">Date</th>
                    <th className="text-right px-4 py-2 font-semibold">Amount</th>
                    <th className="text-center px-4 py-2 font-semibold">Items</th>
                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                    <th className="text-left px-4 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((bill) => (
                    <tr
                      key={bill.id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-3 font-mono font-medium">
                        {bill.supplierBillNo || `Bill-${bill.id}`}
                      </td>
                      <td className="px-4 py-3">{bill.supplier.name}</td>
                      <td className="px-4 py-3">
                        {new Date(bill.billDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        ₹{bill.totalAmount?.toLocaleString() || 0}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="bg-gray-200 text-gray-800 px-2 py-1 rounded text-xs font-medium">
                          {bill.items?.length || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="badge badge-success">
                          {bill.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() =>
                            navigate(
                              `/dashboard/procurement/purchase-bills/${bill.id}`
                            )
                          }
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Showing {(page - 1) * pageSize + 1} to{' '}
                  {Math.min(page * pageSize, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
